/**
 * SearchDeeplinksV2 — redesigned editor for the "search-deeplink"
 * configuration (image + iOS / Android URLs shown when the user
 * lands on the empty search screen).
 *
 * Layout: UploadTile on the left, two TextInputs stacked on the
 * right, coral Save button at the bottom-right. Mirrors the v1
 * 2fr:3fr grid but inside V2 surfaces.
 *
 * Same `searchDeeplinksActions` + `mediaActions` plumbing as v1.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchSearchDeeplinksInfo,
  saveSearchDeeplinksInfo,
} from '../../../actions/searchDeeplinksActions';
import { clearMedia, uploadImage } from '../../../actions/mediaActions';
import { Button } from '../../redesign';
import { FieldBlock, TextInput, UploadTile } from '../../redesign/edit';
import { toast } from '../../../utils/v2Toast';
import { preventEnterFormSubmission } from '../../../utils/formUtils';

const SaveIconSvg = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);

export default function SearchDeeplinksV2() {
  const dispatch = useDispatch();
  const deeplinks = useSelector((state) => state.search.deeplinks);

  const [imageUrl, setImageUrl]   = useState('');
  const [imagePreview, setImagePreview] = useState('');
  const [iosUrl, setIosUrl]       = useState('');
  const [androidUrl, setAndroidUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [edited, setEdited]       = useState(false);

  // Initial fetch.
  useEffect(() => {
    dispatch(fetchSearchDeeplinksInfo());
    dispatch(clearMedia());
  }, [dispatch]);

  // Hydrate when Redux fills in.
  useEffect(() => {
    if (deeplinks) {
      setImageUrl(deeplinks.deeplink_image_url || '');
      setIosUrl(deeplinks.deeplink_ios || '');
      setAndroidUrl(deeplinks.deeplink_android || '');
    }
  }, [deeplinks]);

  // Cleanup local blob preview on unmount / preview swap.
  const previewRef = useRef('');
  previewRef.current = imagePreview;
  useEffect(() => {
    return () => {
      if (previewRef.current) window.URL.revokeObjectURL(previewRef.current);
    };
  }, []);

  const handleImageUpload = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const localPreview = window.URL.createObjectURL(file);
      if (imagePreview) window.URL.revokeObjectURL(imagePreview);
      setImagePreview(localPreview);
      setUploading(true);
      setEdited(true);
      dispatch(uploadImage(file))
        .then((response) => {
          setImageUrl(response.data.image_url);
          setUploading(false);
          dispatch(clearMedia());
        })
        .catch(() => {
          setUploading(false);
          toast.error('Image upload failed. Please try again.');
        });
    },
    [dispatch, imagePreview],
  );

  const handleImageRemove = useCallback(() => {
    if (imagePreview) window.URL.revokeObjectURL(imagePreview);
    setImagePreview('');
    setImageUrl('');
    setEdited(true);
  }, [imagePreview]);

  const handleSubmit = useCallback(
    async (e) => {
      e?.preventDefault?.();
      try {
        await dispatch(saveSearchDeeplinksInfo(imageUrl, iosUrl, androidUrl));
        setEdited(false);
      } catch (_err) {
        toast.error('Failed to save deeplinks information. Please try again.');
      }
    },
    [dispatch, imageUrl, iosUrl, androidUrl],
  );

  const iosMissing     = !iosUrl && edited;
  const androidMissing = !androidUrl && edited;

  return (
    <form onSubmit={handleSubmit} onKeyDown={preventEnterFormSubmission}>
      <div
        className="grid grid-cols-1 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]"
        style={{ gap: 24, alignItems: 'start' }}
      >
        {/* Image */}
        <UploadTile
          label="Deeplink Image"
          hint="Shown above the iOS/Android target link"
          value={imagePreview || imageUrl}
          uploading={uploading}
          accept="image/*"
          onChange={handleImageUpload}
          onRemove={handleImageRemove}
        />

        {/* Fields */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <FieldBlock label="Deeplink iOS" required={edited}>
            <TextInput
              value={iosUrl}
              onChange={(e) => {
                setIosUrl(e.target.value);
                setEdited(true);
              }}
              placeholder="https://apps.apple.com/…"
            />
            {iosMissing && (
              <div className="text-coral-dark" style={{ fontSize: 11.5, marginTop: 4 }}>
                iOS deeplink is required
              </div>
            )}
          </FieldBlock>

          <FieldBlock label="Deeplink Android" required={edited}>
            <TextInput
              value={androidUrl}
              onChange={(e) => {
                setAndroidUrl(e.target.value);
                setEdited(true);
              }}
              placeholder="https://play.google.com/…"
            />
            {androidMissing && (
              <div className="text-coral-dark" style={{ fontSize: 11.5, marginTop: 4 }}>
                Android deeplink is required
              </div>
            )}
          </FieldBlock>
        </div>
      </div>

      <div className="flex" style={{ justifyContent: 'flex-end', marginTop: 16 }}>
        <Button
          variant="coral"
          leadingIcon={SaveIconSvg}
          type="submit"
          disabled={!edited || uploading}
        >
          Save
        </Button>
      </div>
    </form>
  );
}
